import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Settings as SettingsIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ordersService, { Order, OrderStats, OrderStatus, OrderFilters } from '../../services/orders.service';
import shopService, { Store } from '../../services/shop.service';

const Orders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  // Filters
  const [filters, setFilters] = useState<OrderFilters>({
    status: undefined,
    storeId: undefined,
    search: '',
    sortBy: 'date',
    sortOrder: 'desc',
  });

  const statusTabs: { label: string; value: OrderStatus | undefined }[] = [
    { label: t('shop.allOrders'), value: undefined },
    { label: t('shop.orderPending'), value: 'pendente' },
    { label: t('shop.orderConfirmed'), value: 'confirmado' },
    { label: t('shop.orderPreparing'), value: 'preparando' },
    { label: t('shop.orderShipped'), value: 'enviado' },
    { label: t('shop.orderDelivered'), value: 'entregue' },
    { label: t('shop.orderCancelled'), value: 'cancelado' },
  ];

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ordersService.getOrders({
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      });

      if (response.success) {
        setOrders(response.data || []);
        setTotal(response.pagination?.total || 0);
        setNeedsSetup(response.needsSetup || false);
      }
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(t('shop.errorLoadingOrders'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await ordersService.getOrderStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const response = await shopService.getStores({ limit: 100 });
      if (response.success) {
        setStores(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchStores();
  }, [fetchOrders, fetchStats, fetchStores]);

  const handleSetup = async () => {
    setSettingUp(true);
    try {
      await ordersService.setupTables();
      setNeedsSetup(false);
      fetchOrders();
      fetchStats();
    } catch (err: any) {
      setError(t('shop.errorSetupOrders', { message: err.message || t('common.error') }));
    } finally {
      setSettingUp(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setFilters(prev => ({
      ...prev,
      status: statusTabs[newValue].value,
    }));
    setPage(0);
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`/pagalishop/orders/${orderId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Stats Cards
  const statsCards = [
    {
      title: t('shop.totalOrders'),
      value: stats?.total || 0,
      icon: <CartIcon />,
      color: '#2196f3',
    },
    {
      title: t('shop.pendingOrders'),
      value: stats?.pending || 0,
      icon: <FilterIcon />,
      color: '#ff9800',
    },
    {
      title: t('shop.inTransit'),
      value: (stats?.preparing || 0) + (stats?.shipped || 0),
      icon: <ShippingIcon />,
      color: '#9c27b0',
    },
    {
      title: t('shop.deliveredOrders'),
      value: stats?.delivered || 0,
      icon: <TrendingIcon />,
      color: '#4caf50',
    },
    {
      title: t('shop.totalValue'),
      value: ordersService.formatCurrency(stats?.totalValue || 0),
      icon: <MoneyIcon />,
      color: '#00bcd4',
      isText: true,
    },
    {
      title: t('shop.today'),
      value: stats?.todayOrders || 0,
      icon: <CartIcon />,
      color: '#e91e63',
      subtitle: ordersService.formatCurrency(stats?.todayValue || 0),
    },
  ];

  if (needsSetup) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleSetup}
              disabled={settingUp}
              startIcon={settingUp ? <CircularProgress size={16} /> : <SettingsIcon />}
            >
              {settingUp ? t('shop.settingUpNow') : t('shop.setupNow')}
            </Button>
          }
        >
          {t('shop.orderSetupNeeded')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {t('shop.orders')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchOrders();
            fetchStats();
          }}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 2 }} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: `${card.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    color: card.color,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {card.isText ? card.value : card.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.title}
                </Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('shop.orderSearchPlaceholder')}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('shop.storeLabel')}
              value={filters.storeId || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, storeId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <MenuItem value="">{t('shop.allStoresFilter')}</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('shop.dateFrom')}
              value={filters.dateFrom || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('shop.dateTo')}
              value={filters.dateTo || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setFilters({
                  status: undefined,
                  storeId: undefined,
                  search: '',
                  sortBy: 'date',
                  sortOrder: 'desc',
                });
                setSelectedTab(0);
                setPage(0);
              }}
            >
              {t('shop.clear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Status Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {statusTabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('shop.order')}</TableCell>
              <TableCell>{t('shop.storeHeader')}</TableCell>
              <TableCell>{t('shop.client')}</TableCell>
              <TableCell align="right">{t('common.total')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell>{t('common.date')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('shop.loadingOrders')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('shop.noOrders')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      #{order.numero}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.storeName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.clientName || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.clientPhone || order.clientEmail || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {ordersService.formatCurrency(order.total)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ordersService.getStatusLabel(order.status)}
                      color={ordersService.getStatusColor(order.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(order.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('shop.viewDetails')}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewOrder(order.id)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('shop.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('shop.paginationDisplay', { from, to, count })}
        />
      </TableContainer>
    </Box>
  );
};

export default Orders;
